import { useState, useCallback, useEffect } from 'react';
import { Droplets, Check, Info, Wind } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper } from '@/components/lab';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Carbonate Solution Preparation',
    content: `Carbonate solutions contain CO3(2-) ions, which are derived from carbonic acid (H₂CO₃). Carbonates are weak bases and react readily with acids. The carbonate ion has a trigonal planar geometry with resonance structures distributing the negative charge across all three oxygen atoms. Common carbonate compounds include sodium carbonate (Na₂CO₃, "washing soda") and calcium carbonate (CaCite, limestone).`
  },
  2: {
    title: 'The Acid Test for Carbonates',
    content: `When H₂SO₄ is added to carbonate, a rapid acid-base reaction occurs: CO3(2-) + 2H(+) -> H₂O + CO₂(g). The effervescence (bubbling) you observe is carbon dioxide gas escaping. This is a classic qualitative test for carbonates. Concentrated H₂SO₄ is used because it provides a strong driving force for the reaction and doesn't introduce interfering anions.`
  },
  3: {
    title: 'Observing Gas Evolution',
    content: `The vigorous bubbling indicates rapid CO₂ production. Gas evolution reactions are often used in qualitative analysis because they're easy to observe. The reaction is exothermic, and shaking increases contact between reactants, accelerating gas release. The CO₂ produced is odorless and colorless, so we need a secondary test (Ba(OH)₂) to confirm its identity.`
  },
  4: {
    title: 'Setting Up the Barium Hydroxide Test',
    content: `A fresh carbonate solution is needed because the previous tube's CO₂ has already escaped. The Ba(OH)₂ test is more specific than observing bubbles alone - many compounds produce gases with acids. Barium hydroxide is a strong base that reacts specifically with CO₂ to form barium carbonate precipitate, providing definitive confirmation.`
  },
  5: {
    title: 'Using Dilute HCl for CO₂ Generation',
    content: `6M HCl is used instead of H₂SO₄ for the confirmatory test because it produces a steadier stream of CO₂. The reaction CO3(2-) + 2HCl -> 2Cl(-) + H₂O + CO₂ generates gas more controllably. HCl is less hazardous than concentrated H₂SO₄ and the chloride ions don't interfere with the barium carbonate test.`
  },
  6: {
    title: 'Positioning the Barium Hydroxide Drop',
    content: `The Ba(OH)₂ drop must be positioned just above the tube opening to capture the rising CO₂ gas. The drop acts as a "trap" for the gas. Barium hydroxide solution is clear and colorless initially. The position is critical - too far away and insufficient CO₂ contacts the drop; too close and liquid splashing could contaminate it.`
  },
  7: {
    title: 'The Barium Carbonate Precipitation',
    content: `When CO₂ contacts the Ba(OH)₂ drop, barium carbonate precipitates: CO₂ + Ba(OH)₂ -> BaCO₃(s) + H₂O. BaCO₃ is a white, insoluble solid that makes the clear drop turn cloudy or milky. This is a highly specific test for CO₂ because few other gases produce this reaction with barium hydroxide.`
  },
  8: {
    title: 'Confirming Carbonate Presence',
    content: `A positive carbonate test requires BOTH observations: (1) effervescence with acid, and (2) cloudiness in the Ba(OH)₂ drop. The first test shows gas evolution; the second confirms the gas is CO₂. This two-step approach eliminates false positives from other gas-producing reactions. Record "positive" for carbonate when both criteria are met.`
  }
};

interface Part2CarbonateProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part2Carbonate({ onStepComplete, currentStep = 1 }: Part2CarbonateProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [co3SolutionAdded, setCo3SolutionAdded] = useState(false);
  const [h2so4Added, setH2so4Added] = useState(false);
  const [isDropping, setIsDropping] = useState(false);
  const [bubbling, setBubbling] = useState(false);
  const [shaken, setShaken] = useState(false);
  const [baoh2TestPrepared, setBaoh2TestPrepared] = useState(false);
  const [hclAdded, setHclAdded] = useState(false);
  const [dropPositioned, setDropPositioned] = useState(false);
  const [dropCloudy, setDropCloudy] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  // Bubbling animation effect
  useEffect(() => {
    if (h2so4Added && !shaken) {
      setBubbling(true);
    }
  }, [h2so4Added, shaken]);

  const handleAddCO3Solution = useCallback(() => {
    setCo3SolutionAdded(true);
    if (onStepComplete) onStepComplete('carbonate-1');
  }, [onStepComplete]);

  const handleAddH2SO4 = useCallback(() => {
    if (co3SolutionAdded) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setH2so4Added(true);
        if (onStepComplete) onStepComplete('carbonate-2');
      }, 500);
    }
  }, [co3SolutionAdded, onStepComplete]);

  const handleShake = useCallback(() => {
    if (h2so4Added) {
      setShaken(true);
      if (onStepComplete) onStepComplete('carbonate-3');
    }
  }, [h2so4Added, onStepComplete]);

  const handlePrepareBaOH2Test = useCallback(() => {
    if (shaken) {
      setBaoh2TestPrepared(true);
      if (onStepComplete) onStepComplete('carbonate-4');
    }
  }, [shaken, onStepComplete]);

  const handleAddHCl = useCallback(() => {
    if (baoh2TestPrepared) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setHclAdded(true);
        if (onStepComplete) onStepComplete('carbonate-5');
      }, 500);
    }
  }, [baoh2TestPrepared, onStepComplete]);

  const handlePositionDrop = useCallback(() => {
    if (hclAdded) {
      setDropPositioned(true);
      if (onStepComplete) onStepComplete('carbonate-6');
    }
  }, [hclAdded, onStepComplete]);

  const handleObserveDrop = useCallback(() => {
    if (dropPositioned) {
      setDropCloudy(true);
      if (onStepComplete) onStepComplete('carbonate-7');
    }
  }, [dropPositioned, onStepComplete]);

  const handleRecordResult = useCallback(() => {
    if (dropCloudy) {
      setResultRecorded(true);
      if (onStepComplete) onStepComplete('carbonate-8');
    }
  }, [dropCloudy, onStepComplete]);

  return (
    <LabBench
      experimentName="Carbonate Confirmation Test"
      completedCount={[co3SolutionAdded, h2so4Added, shaken, baoh2TestPrepared, hclAdded, dropPositioned, dropCloudy, resultRecorded].filter(Boolean).length}
      totalSteps={8}
      equipment={['CO3 solution', 'Concentrated H₂SO₄', '6M HCl', 'Ba(OH)₂', 'Test tubes']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="carbonate-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Test Tube with CO3 solution */}
          <div className="equipment-section">
            <svg viewBox="0 0 70 150" width={70} height={150}>
              <defs>
                <linearGradient id="tube-glass-co3" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="35" cy="15" rx="16" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M19 15 L19 115 Q19 130 35 130 Q51 130 51 115 L51 15" fill="url(#tube-glass-co3)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill */}
              {co3SolutionAdded && (
                <path
                  d="M21 50 L21 113 Q21 127 35 127 Q49 127 49 113 L49 50 Z"
                  fill="#F5F5F5"
                  opacity="0.8"
                />
              )}

              {/* Bubbles when acid is added */}
              {bubbling && !shaken && (
                <g>
                  {[...Array(8)].map((_, i) => (
                    <circle
                      key={i}
                      cx={25 + (i % 3) * 10}
                      cy={100}
                      r={2 + Math.random()}
                      fill="rgba(255,255,255,0.8)"
                      stroke="#E0E0E0"
                      strokeWidth="0.5"
                    >
                      <animate
                        attributeName="cy"
                        values="100;50;20"
                        dur={`${0.8 + i * 0.2}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0.8;0.5;0"
                        dur={`${0.8 + i * 0.2}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                  ))}
                </g>
              )}

              {/* Gas escaping indication */}
              {bubbling && (
                <g opacity="0.6">
                  <path d="M30 10 Q25 0 30 -5" fill="none" stroke="#BDBDBD" strokeWidth="1.5">
                    <animate attributeName="d" values="M30 10 Q25 0 30 -5;M30 10 Q35 0 30 -5;M30 10 Q25 0 30 -5" dur="1s" repeatCount="indefinite" />
                  </path>
                  <path d="M40 10 Q45 0 40 -5" fill="none" stroke="#BDBDBD" strokeWidth="1.5">
                    <animate attributeName="d" values="M40 10 Q45 0 40 -5;M40 10 Q35 0 40 -5;M40 10 Q45 0 40 -5" dur="0.8s" repeatCount="indefinite" />
                  </path>
                </g>
              )}

              {/* Glass shine */}
              <line x1="23" y1="20" x2="23" y2="105" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">{baoh2TestPrepared ? 'Fresh CO3' : 'CO3 Solution'}</span>
          </div>

          {/* H₂SO₄ Dropper */}
          <div className="equipment-section">
            <Dropper
              label="Conc. H₂SO₄"
              liquidColor="#F5F5F5"
              onClick={co3SolutionAdded && !h2so4Added ? handleAddH2SO4 : undefined}
              active={co3SolutionAdded && !h2so4Added}
              dropping={isDropping && !h2so4Added}
              disabled={!co3SolutionAdded || h2so4Added}
            />
          </div>

          {/* HCl Dropper for Ba(OH)₂ test */}
          {baoh2TestPrepared && (
            <div className="equipment-section">
              <Dropper
                label="6M HCl"
                liquidColor="#F5F5F5"
                onClick={!hclAdded ? handleAddHCl : undefined}
                active={!hclAdded}
                dropping={isDropping && baoh2TestPrepared}
                disabled={hclAdded}
              />
            </div>
          )}

          {/* Ba(OH)₂ Drop Setup */}
          {hclAdded && (
            <div className="equipment-section baoh2-setup">
              <svg viewBox="0 0 80 100" width={80} height={100}>
                <defs>
                  <radialGradient id="drop-grad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={dropCloudy ? '#E0E0E0' : '#F5F5F5'} />
                    <stop offset="100%" stopColor={dropCloudy ? '#BDBDBD' : '#EEEEEE'} />
                  </radialGradient>
                </defs>

                {/* Glass rod */}
                <rect x="35" y="5" width="10" height="50" fill="#B0BEC5" rx="2" />

                {/* Ba(OH)₂ drop on rod tip */}
                <ellipse
                  cx="40"
                  cy="60"
                  rx={dropPositioned ? 12 : 8}
                  ry={dropPositioned ? 8 : 5}
                  fill="url(#drop-grad)"
                  stroke={dropCloudy ? '#9E9E9E' : '#BDBDBD'}
                  strokeWidth="0.5"
                >
                  {dropPositioned && !dropCloudy && (
                    <animate
                      attributeName="ry"
                      values="8;10;8"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  )}
                </ellipse>

                {/* Cloud particles if CO₂ absorbed */}
                {dropCloudy && (
                  <g opacity="0.6">
                    <circle cx="35" cy="58" r="2" fill="#BDBDBD" />
                    <circle cx="45" cy="62" r="1.5" fill="#BDBDBD" />
                    <circle cx="40" cy="55" r="1" fill="#BDBDBD" />
                  </g>
                )}

                {/* Label */}
                <text x="40" y="85" fontSize="8" fill="var(--text-secondary)" textAnchor="middle">Ba(OH)₂ Drop</text>
              </svg>
            </div>
          )}
        </div>

        {/* Status Messages */}
        <div className="status-section">
          {!co3SolutionAdded && (
            <div className="instruction-card">
              <span className="step-badge">Step 1</span>
              <p>Add 3 mL of <strong>carbonate solution</strong> to a clean test tube.</p>
              <button className="action-btn" onClick={handleAddCO3Solution}>
                <Droplets size={16} />
                Add CO3 Solution
              </button>
            </div>
          )}

          {co3SolutionAdded && !h2so4Added && (
            <div className="instruction-card">
              <span className="step-badge">Step 2</span>
              <p>Add <strong>2 drops of concentrated H₂SO₄</strong> (work in fume hood!).</p>
              <p className="hint">Click the H₂SO₄ dropper.</p>
            </div>
          )}

          {h2so4Added && !shaken && (
            <div className="instruction-card">
              <span className="step-badge">Step 3</span>
              <p>Carefully <strong>shake</strong> the tube and observe gas evolution.</p>
              <div className="observation-note">
                <Wind size={16} />
                <span>Notice the vigorous effervescence (bubbling) - this is CO₂ gas!</span>
              </div>
              <button className="action-btn" onClick={handleShake}>
                <Check size={16} />
                Shake and Observe
              </button>
            </div>
          )}

          {shaken && !baoh2TestPrepared && (
            <div className="instruction-card">
              <span className="step-badge">Step 4</span>
              <p>Prepare a <strong>new tube</strong> with fresh carbonate solution for the Ba(OH)₂ test.</p>
              <button className="action-btn" onClick={handlePrepareBaOH2Test}>
                <Droplets size={16} />
                Prepare New Tube
              </button>
            </div>
          )}

          {baoh2TestPrepared && !hclAdded && (
            <div className="instruction-card">
              <span className="step-badge">Step 5</span>
              <p>Add <strong>2 drops of 6M HCl</strong> to generate CO₂ gas.</p>
              <p className="hint">Click the HCl dropper.</p>
            </div>
          )}

          {hclAdded && !dropPositioned && (
            <div className="instruction-card">
              <span className="step-badge">Step 6</span>
              <p>Position a <strong>drop of Ba(OH)₂</strong> solution above the tube opening.</p>
              <button className="action-btn" onClick={handlePositionDrop}>
                <Droplets size={16} />
                Position Ba(OH)₂ Drop
              </button>
            </div>
          )}

          {dropPositioned && !dropCloudy && (
            <div className="instruction-card">
              <span className="step-badge">Step 7</span>
              <p>Observe if the <strong>clear drop becomes cloudy</strong>.</p>
              <button className="action-btn" onClick={handleObserveDrop}>
                <Check size={16} />
                Observe Drop
              </button>
            </div>
          )}

          {dropCloudy && !resultRecorded && (
            <div className="instruction-card success">
              <span className="step-badge">Step 8</span>
              <p>Record the <strong>positive result</strong> for carbonate confirmation.</p>
              <button className="action-btn success" onClick={handleRecordResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {resultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>CO3 2- Confirmed!</strong></p>
                <p>The cloudy drop confirms CO₂ gas reacted with Ba(OH)₂ to form BaCO₃.</p>
              </div>
            </div>
          )}
        </div>

        {/* Reaction indicator */}
        {h2so4Added && (
          <div className="reaction-indicator">
            <Wind size={16} />
            <span>Gas Evolution: {bubbling ? 'CO₂ gas escaping!' : 'Reaction complete'}</span>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> Carbonate-Acid Reaction</h4>
          <p>
            When carbonate ions react with acids, carbon dioxide gas is evolved.
            The Ba(OH)₂ test confirms that the gas is CO₂ - it reacts to form
            insoluble barium carbonate, making the drop cloudy.
          </p>
          <div className="equation">
            CO<sub>3</sub><sup>2-</sup> + 2H<sup>+</sup> → H<sub>2</sub>O + CO<sub>2</sub>(g) ↑
          </div>
          <div className="equation secondary">
            CO<sub>2</sub> + Ba(OH)<sub>2</sub> → BaCO<sub>3</sub>(s) + H<sub>2</sub>O
          </div>
        </div>

        <style>{`
          .carbonate-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 650px;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .baoh2-setup {
            background: var(--bg-elevated);
            padding: 10px;
            border-radius: 12px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .observation-note {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px;
            background: rgba(99, 102, 241, 0.1);
            border-radius: 8px;
            margin: 8px 0;
            font-size: 12px;
            color: var(--primary);
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .reaction-indicator {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 14px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 8px;
          }

          .theory-box .equation.secondary {
            font-size: 12px;
            margin-bottom: 0;
          }

          .info-buttons-row {
            display: flex;
            gap: 8px;
            justify-content: center;
            flex-wrap: wrap;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {/* Info Buttons */}
        <div className="info-buttons-row">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((stepId) => (
            stepInfoContent[stepId] && (
              <button
                key={stepId}
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(stepId); }}
                title={stepInfoContent[stepId].title}
              >
                ?
              </button>
            )
          ))}
        </div>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp3"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
